/* eslint-disable @next/next/no-img-element */
'use client';
import { getInitCommentListPage } from '@/apis/dp/service';
import AllCommentList from '@/components/customerReviews/allCommentList';
import ScoreRating from '@/components/customerReviews/scoreRating';
import { IntlDialog } from '@/components/intlMui';
import JDIPrice from '@/components/price/index';
import Assurance from '@/components/product/assurance';
import { CN_PRODUCT, dataExpTagConfig } from '@/components/product/const';
import ServiceList from '@/components/product/serviceList';
import { useTranslation } from '@/i18n/client';
import ArrowIcon from '@/images/icon/arrow-default.svg';
import KlranaIcon from '@/images/icon/product-klrana.svg';
import { ServiceFloorWrapper, SkuDetailWrapper } from '@/styles/product';
import { EXPOSURE_RECORD, sendClickTrack } from '@/tracks/25217';
import { useParams } from 'next/navigation';
import { useState } from 'react';
import PopoverWithIcon from './popoverWithIcon';
import type { LocalInfoType } from '@/apis/common.types';
import { ErrorBoundary } from 'react-error-boundary';
import AddressSelector from './addressSelector';
import BuyOrAddToCar from './buyOrAddToCart';
import type { ServiceFloorData } from './interface';
import KlranaDialog from './klranaDialog';
import SelectModelGroup from './selectModelGroup';
import SkuCoupon from './skuCoupon';
import FinalPriceDrawerContent from './finalPriceDrawerContent';
import IntegralDialog from './integralDialog/integralDialog';
// styles
import { styled as muiStyled } from '@mui/material/styles';
import PromotionTag from './promotionTag';
import cn from 'clsx';
import CDNImage from '../cdnImage/cdnImage';
import PlusFloor from './plusFloor';
import UnitPrice from './unitPrice';
import PreSale from './preSale';
import { eventTrackingUtils } from '@/utils/eventTrack/eventTrackingUtils';
import { PROMOTION_TYPE } from '@/apis/dp/floor.interface';
import BestSeller from './bestSeller';

enum PricePromotion {
  PLUS = 'plus',
}
interface SkuDetailProps {
  floors: any;
  localInfo: LocalInfoType;
  autoClaimCoupon?: any;
}

export default function SkuDetail(props: SkuDetailProps) {
  const { t } = useTranslation();
  const { floors, localInfo } = props || {};
  const title = floors?.productSkuNameFloor?.data['C-M#ViewTitleItem&core']?.title;
  const productCommentSaleFloor = floors?.productCommentSaleFloor?.data;
  const productPriceFloor = floors?.productPriceFloor?.data;

  const { star, commentCount, starShow, saleAmount } = productCommentSaleFloor || {};
  const {
    percentReductionDescIntergation,
    mainPrice,
    crossOffPrice,
    memberPromotion,
    plusIconUrl,
    finalPricePop,
    estimatePointTextIntergation,
    opcPointRuleTitle,
    opcPointRuleH5SceneId,
    pricePlusTips,
  }: Record<string, any> = productPriceFloor?.['C-M#priceViewItem&priceInfo'] || {};

  // 排行榜数据
  const bestSellerFloor = floors?.productBestSellerFloor?.data;
  const rankText = bestSellerFloor?.rankText;
  const category = bestSellerFloor?.categoryName;
  const categoryPageUrl = bestSellerFloor?.destinationPageUrl;
  // 非会员引导

  const { memberPurchaseGuideInfo } = floors?.productMemberPurchaseGuideFloor?.data || {};
  // 分期支付
  const productPaymentFloor = floors?.productPaymentFloor?.data;
  const payText = productPaymentFloor?.payTextIntergation;
  const klranaContent = productPaymentFloor?.popUpText;
  //年龄限制楼层
  const productComplianceFloor = floors.productComplianceFloor?.data;
  // 服务楼层
  const serviceData: ServiceFloorData = floors?.productServiceItemsFloor?.data;

  // 预售楼层
  const promotionInfo = floors?.productPromotionFloor?.data?.promotionInfo;

  // console.log("===========productFloor============", floors);

  const [allCommentInfo, setAllCommentInfo] = useState<any>();
  const { sku: skuId } = useParams<{ sku: string }>();
  const [open, setOpen] = useState<boolean>(false);
  const [openKlrana, setOpenKlrana] = useState<boolean>(false);
  // const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const showCrossOffPrice = crossOffPrice && crossOffPrice.trim() !== mainPrice?.trim();
  const [pointAnchorEl, setPointAnchorEl] = useState<HTMLButtonElement | null>(null);
  const floorsWithSkuIdProps = {
    floors,
    skuId,
  };

  const openReviewListDialog = async () => {
    sendClickTrack.Productdetail_StarRating_Click({
      mainskuid: skuId,
      ratingNum: String(star),
      reviewsNum: String(commentCount),
      SalesNum: String(saleAmount),
    });
    const res = await getInitCommentListPage({ skuId });
    if (res.code === '200') {
      setAllCommentInfo(res?.data);
    }
    setOpen(true);
  };

  const closeReviewListDialog = () => {
    setOpen(false);
  };

  // const handlePopoverClick = (event: React.MouseEvent<HTMLButtonElement>)=> {
  //   setAnchorEl(event.currentTarget);
  // }

  // const handlePopoverClose = () => {
  //   setAnchorEl(null);
  // };

  const handlePointPopoverClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPointAnchorEl(event.currentTarget);
  };

  const handlePointPopoverClose = () => {
    setPointAnchorEl(null);
  };

  const openKlranaDialog = (displayText: string) => {
    setOpenKlrana(true);
    sendClickTrack.Productdetail_Installment_Click({
      mainskuid: skuId,
      displayText,
    });
  };

  const getReviewsDesc = () => {
    return commentCount == '1'
      ? `${t('dp.commentCount.review', { count: 1 })}`
      : `${t('dp.commentCount.review', { count: 5 })}`;
  };
  // const isPopoverOpen = Boolean(anchorEl);
  // const id = isPopoverOpen ? 'simple-popover' : undefined;
  return (
    <SkuDetailWrapper>
      {!!title && <h1 className={CN_PRODUCT('sku-title')}>{title}</h1>}
      <ErrorBoundary fallback={null}>
        {(bestSellerFloor || commentCount || saleAmount) && (
          <div className={CN_PRODUCT('sku-auxiliary-info')}>
            {productCommentSaleFloor && commentCount && (
              <div
                className={CN_PRODUCT('sku-score')}
                exptag={'exp|' + EXPOSURE_RECORD.Productdetail_StarRating_Expo}
                data-exptag-config={dataExpTagConfig}
                data-exptag-json-param={JSON.stringify({
                  mainskuid: skuId,
                  ratingNum: String(star),
                  reviewsNum: String(commentCount),
                  SalesNum: String(saleAmount),
                })}
              >
                <ScoreRating
                  starShow={starShow}
                  value={star}
                  extra={
                    saleAmount ? (
                      <>
                        <span className='sale-amount margin-left'>
                          {t('dp.itemSold', { saleAmount })}
                        </span>
                        <span className='comment-count' onClick={openReviewListDialog}>
                          {commentCount} {getReviewsDesc()}
                          <ArrowIcon />
                        </span>
                      </>
                    ) : (
                      <span className='comment-count'>
                        {commentCount} {getReviewsDesc()}
                        <ArrowIcon />
                      </span>
                    )
                  }
                />
              </div>
            )}
            {bestSellerFloor && <BestSeller bestSellerFloor={bestSellerFloor} skuId={skuId} />}
            {productCommentSaleFloor && !commentCount && saleAmount && (
              <div className={CN_PRODUCT('sku-score')}>
                <div className='sale-amount'>{t('dp.itemSold', { saleAmount })}</div>
              </div>
            )}
            <IntlDialog
              onClose={closeReviewListDialog}
              aria-labelledby='customized-dialog-title'
              open={open}
              // title='Reviews'
              title={t('review.reviewTitle')}
              content={<AllCommentList data={allCommentInfo} skuId={skuId} />}
              width={800}
              showButtonAction={false}
            />
          </div>
        )}
      </ErrorBoundary>
      {/* type: -8：预售 */}
      {promotionInfo?.type === PROMOTION_TYPE.PRESALE && (
        <PreSale productPromotionFloor={promotionInfo} />
      )}
      {/* 品牌标签 */}
      <PromotionTag floors={floors} />
      {/* {价格} */}
      {mainPrice && (
        <div
          className={cn(CN_PRODUCT('sku-price'), {
            [PricePromotion.PLUS]: memberPromotion,
          })}
        >
          {/* {memberPromotion && (
            <div className='promotion-frame'>
              <CDNImage
                className='promotion-icon'
                src={plusIconUrl}
                width={40}
                height={20}
                alt='plus'
              />
            </div>
          )} */}
          <div className={CN_PRODUCT('sku-price-real')}>
            <JDIPrice
              locale={localInfo?.languageCode}
              currency={localInfo?.currency}
              price={mainPrice}
              uiType={1}
              symbolStyle={{
                fontSize: '24px',
                lineHeight: '24px',
              }}
              integerStyle={{
                fontSize: '32px',
                lineHeight: '40px',
              }}
              containerStyle={{
                fontSize: '32px',
                lineHeight: '40px',
                // color: memberPromotion ? '#7014CC' : showCrossOffPrice ? '#CC0C1C' : '#1A1A1A',
                color: memberPromotion || showCrossOffPrice ? '#CC0C1C' : '#1A1A1A',
                fontWeight: 700,
              }}
            />
          </div>
          {/* {showCrossOffPrice && (
            <div className={CN_PRODUCT('sku-price-origin')}>
              <JDIPrice
                locale={localInfo?.languageCode}
                currency={localInfo?.currency}
                price={crossOffPrice}
                uiType={2}
                lineThrough
                containerStyle={{
                  fontSize: '24px',
                  lineHeight: '24px',
                  color: '#1A1A1A',
                  fontWeight: 700,
                }}
              />
            </div>
          )} */}
          <div className='opt-zone'>
            {/* {percentReductionDesc && (
              <div className={CN_PRODUCT('sku-price-tag')}>{percentReductionDesc}</div>
            )} */}
            {memberPromotion && <div className='frameText'>{pricePlusTips}</div>}
            {finalPricePop?.detailList && (
              <PopoverWithIcon>
                <FinalPriceDrawerContent
                  localInfo={localInfo}
                  {...finalPricePop}
                  // onConfirm={()=>{
                  //   handlePopoverClose();
                  // }}
                />
              </PopoverWithIcon>
            )}
          </div>
        </div>
      )}
      {/* 百分比减少说明 新增折扣会员价格显示*/}
      <div className={CN_PRODUCT('sku-tagIntegral')}>
        {showCrossOffPrice && (
          <div className={CN_PRODUCT('sku-tagIntegral-price-origin')}>
            <JDIPrice
              locale={localInfo?.languageCode}
              currency={localInfo?.currency}
              price={crossOffPrice}
              uiType={2}
              lineThrough
              containerStyle={{
                fontSize: '16px',
                lineHeight: '24px',
                color: '#1A1A1A',
                fontWeight: 600,
              }}
            />
          </div>
        )}
        {showCrossOffPrice && percentReductionDescIntergation && (
          <span className={CN_PRODUCT('sku-tagIntegral-line')}>|</span>
        )}
        {percentReductionDescIntergation && (
          <div className={CN_PRODUCT('sku-tagIntegral-tag')}>{percentReductionDescIntergation}</div>
        )}
      </div>
      {/* 积分说明 */}
      {estimatePointTextIntergation && (
        <div className={CN_PRODUCT('sku-integral')}>
          <span>{estimatePointTextIntergation}</span>
          <PopoverWithIcon>
            <IntegralDialog
              title={opcPointRuleTitle}
              sceneId={opcPointRuleH5SceneId}
              onConfirm={handlePointPopoverClose}
            />
          </PopoverWithIcon>
          {/* <TipSvg aria-describedby={pointId} onClick={handlePointPopoverClick} /> */}
          {/* <WidthArrowPopover
            // id={pointId}
            // open={isPointPopoverOpen}
            // anchorEl={pointAnchorEl}
            // onClose={handlePointPopoverClose}
            // anchorOrigin={{
            //   vertical: 'bottom',
            //   horizontal: 'center',
            // }}
            // transformOrigin={{
            //   vertical: 'top',
            //   horizontal: 'center',
            // }}
          >
            <IntegralDialog
              title={opcPointRuleTitle}
              sceneId={opcPointRuleH5SceneId}
              onConfirm={handlePointPopoverClose}
            />
          </WidthArrowPopover> */}
        </div>
      )}
      {/* 单位到手价 */}
      <ErrorBoundary fallback={null}>
        {floors?.productUnitPriceFloor?.data?.unitPrice && (
          <UnitPrice productUnitPriceFloor={floors?.productUnitPriceFloor?.data} />
        )}
      </ErrorBoundary>
      {/* {分期支付} */}
      {productPaymentFloor && (
        <div
          className={CN_PRODUCT('sku-klrana')}
          onClick={() => openKlranaDialog(payText)}
          exptag={'exp|' + EXPOSURE_RECORD.Productdetail_Installment_Expo}
          data-exptag-config={dataExpTagConfig}
          data-exptag-json-param={JSON.stringify({
            mainskuid: skuId,
            displayText: payText,
          })}
        >
          <span dangerouslySetInnerHTML={{ __html: payText }} />
          <KlranaIcon />
        </div>
      )}
      {/* plus 购买引导 */}
      <PlusFloor {...(memberPurchaseGuideInfo || {})} skuId={skuId} />
      <KlranaDialog
        open={openKlrana}
        closeFn={() => setOpenKlrana(false)}
        content={klranaContent}
      />
      {/* {优惠券 活动} */}
      <SkuCoupon floors={floors} skuId={skuId} localInfo={localInfo} />
      <ErrorBoundary fallback={null}>
        <SelectModelGroup floors={floors} spuTitle={title} />
      </ErrorBoundary>
      <BuyOrAddToCar
        floors={floors}
        serviceData={serviceData}
        localInfo={localInfo}
        autoClaimCoupon={props?.autoClaimCoupon}
      />
      <AddressSelector {...floorsWithSkuIdProps} />

      {serviceData?.serviceItems?.length && (
        <ServiceFloorWrapper>
          <h2 className='service-title'>{serviceData?.title}</h2>
          <ServiceList
            serviceData={serviceData}
            useShareCheck={true}
            mainSkuId={skuId}
            localInfo={localInfo}
          />
        </ServiceFloorWrapper>
      )}
      <Assurance {...floorsWithSkuIdProps} />
    </SkuDetailWrapper>
  );
}
